<template>
  <v-app-bar color="secondary" density="compact" :flat="true">
    <v-toolbar-title class="text-button">{{ icdPresentationString }}: {{ icdDescription }} - {{ selected_menu_item.title }}</v-toolbar-title>
    <v-btn variant="text" icon="mdi-filter"
           @click.stop="filter_drawer = !filter_drawer"
           :disabled="dashboard_1.visible || dashboard_3.visible || dashboard_4.visible || dashboard_5.visible || dashboard_6.visible"></v-btn>
    <v-btn variant="text" icon="mdi-information"
           @click.stop="information_drawer = !information_drawer"
           :disabled="dashboard_6.visible"></v-btn>

    <template v-slot:extension>
      <v-tabs v-model="selected_menu_item" align-tabs="title" show-arrows>
        <v-tab v-for="menu_item in menu_items" :key="menu_item.dashboard" :value="menu_item"
               :disabled="menu_item.accessRight && !isAuthorized(menu_item.accessRight)">
          {{ menu_item.title }}
        </v-tab>
      </v-tabs>
    </template>
  </v-app-bar>

  <v-window v-model="selected_menu_item" direction="horizontal">
    <v-window-item :key="dashboard_1.dashboard" :value="dashboard_1" transition="fade-transition"
                   reverse-transition="fade-transition"
                   @group:selected="(event) => {
                     this.dashboard_1.visible = event.value;
                     this.filter_drawer &&= !event.value;
                   }">
      <InternalOrderCancerProjectOverview :icd="icd"
                                          :disease-description="diseaseDescription"
                                          :project-description="projectDescription"
                                          :visible="this.dashboard_1.visible"
                                          v-if="!this.initializing"></InternalOrderCancerProjectOverview>
    </v-window-item>
    <v-window-item :key="dashboard_2.dashboard" :value="dashboard_2" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_2.visible = event.value">
      <InternalOrderCancerDemographicBaseline :icd="icd"
                                              :disease-description="diseaseDescription"
                                              :age-at-diagnosis="this.selected_age_at_diagnosis"
                                              :sex="this.selected_genders"
                                              :diagnosis-date="this.selected_diagnosis_date"
                                              :visible="this.dashboard_2.visible"
                                              v-on:ageGroupSelected="toggleAllOneAgeGroups($event)"
                                              v-on:genderSelected="toggleAllOneGenders($event)"
                                              v-if="!this.initializing"></InternalOrderCancerDemographicBaseline>
    </v-window-item>
    <v-window-item :key="dashboard_3.dashboard" :value="dashboard_3" transition="fade-transition"
                   reverse-transition="fade-transition"
                   @group:selected="(event) => {
                     this.dashboard_3.visible = event.value;
                     this.filter_drawer &&= !event.value;
                   }">
      <InternalOrderCancerTreatmentPaths :icd="icd"
                                         :visible="this.dashboard_3.visible" v-if="!this.initializing"></InternalOrderCancerTreatmentPaths>
    </v-window-item>
    <v-window-item :key="dashboard_4.dashboard" :value="dashboard_4" transition="fade-transition"
                   reverse-transition="fade-transition"
                   @group:selected="(event) => {
                     this.dashboard_4.visible = event.value;
                     this.filter_drawer &&= !event.value;
                   }">
      <InternalOrderCancerOverallSurvival :icd="icd"
                                          :visible="this.dashboard_4.visible" v-if="!this.initializing"></InternalOrderCancerOverallSurvival>
    </v-window-item>
    <v-window-item :key="dashboard_5.dashboard" :value="dashboard_5" transition="fade-transition"
                   reverse-transition="fade-transition"
                   @group:selected="(event) => {
                     this.dashboard_5.visible = event.value;
                     this.filter_drawer &&= !event.value;
                   }">
      <InternalOrderCancerTreatmentPatternInterventions :icd="icd" :visible="this.dashboard_5.visible" v-if="!this.initializing">
      </InternalOrderCancerTreatmentPatternInterventions>
    </v-window-item>
    <v-window-item :key="dashboard_6.dashboard" :value="dashboard_6" transition="fade-transition"
                   reverse-transition="fade-transition"
                   @group:selected="(event) => {
                     this.dashboard_6.visible = event.value;
                     this.information_drawer &&= !event.value;
                     this.filter_drawer &&= !event.value;
                   }">
      <ClinicalTrials :mesh-ids="meshIds"
                      :mesh-description="meshDescription"
                      :visible="this.dashboard_6.visible" v-if="!this.initializing"></ClinicalTrials>
    </v-window-item>
  </v-window>

  <v-navigation-drawer v-model="information_drawer"
                       v-bind:width="500"
                       :disable-resize-watcher="true"
                       :disable-route-watcher="true"
                       location="right"
                       temporary>
    <v-list density="compact" nav>
      <v-list-item prepend-icon="mdi-information" title="Information about the data set" nav></v-list-item>
    </v-list>

    <v-divider></v-divider>

    <v-list density="compact" nav>
      <v-list-item><strong>Population definition</strong></v-list-item>
      <v-list-item v-for="(item, index) in populationDefinition" :key="index" class="text-block">
        <v-list-item-title><strong>{{ item.title }}</strong></v-list-item-title>
        <div class="mt-2 text-body-2">{{ item.content }}</div>
      </v-list-item>
    </v-list>
  </v-navigation-drawer>

  <v-navigation-drawer v-model="filter_drawer"
                       v-bind:width="500"
                       :disable-resize-watcher="true"
                       :disable-route-watcher="true"
                       location="right">
    <v-card class="mx-auto epix-filter" elevation="0" ref="filters">
      <v-row no-gutters>
        <v-col>
          <v-list nav dense light>
            <v-list-item prepend-icon="mdi-filter" title="Filters" nav></v-list-item>
            <v-divider></v-divider>
            <v-form ref="filter_form">
              <v-row>
                <v-col cols="9">
                  <v-combobox v-model="selected_age_at_diagnosis" :items="select_age_at_diagnosis_items"
                              label="Age at first diagnosis" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              multiple></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneAgeGroups">All/None</v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="9">
                  <v-combobox v-model="selected_genders" :items="select_gender_items"
                              label="Sex" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              multiple></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneGenders">All/None</v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-list>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col class="text-right">
          <vue-to-print :content="() => this.$refs['filters'].$el" :document-title="icd + '-' + 'filters'"
                        remove-after-print>
            <template v-slot:trigger>
              <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
            </template>
          </vue-to-print>
        </v-col>
      </v-row>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import {defineComponent} from "vue";
import axios from "axios";
import InternalOrderCancerProjectOverview from "@/components/InternalOrderCancerProjectOverview.vue";
import InternalOrderCancerDemographicBaseline from '@/components/InternalOrderCancerDemographicBaseline.vue'
import InternalOrderCancerOverallSurvival from '@/components/InternalOrderCancerOverallSurvival.vue'
import InternalOrderCancerTreatmentPatternInterventions from '@/components/InternalOrderCancerTreatmentPatternInterventions.vue'
import InternalOrderCancerTreatmentPaths from '@/components/InternalOrderCancerTreatmentPaths.vue'
import ClinicalTrials from "@/components/ClinicalTrials.vue";
import { VueToPrint } from "vue-to-print";

const DASHBOARD_1 = {
  title: 'Project overview',
  dashboard: 'Dashboard1',
  visible: true
};

const DASHBOARD_2 = {
  title: 'Epidemiology and baseline characteristics',
  dashboard: 'Dashboard2',
  visible: false
};

const DASHBOARD_3 = {
  title: 'Treatment pattern',
  dashboard: 'Dashboard3',
  visible: false
};

const DASHBOARD_4 = {
  title: 'Overall survival',
  dashboard: 'Dashboard4',
  visible: false
};

const DASHBOARD_5 = {
  title: 'Healthcare resource utilization',
  dashboard: 'Dashboard5',
  visible: false
};

const DASHBOARD_6 = {
  title: 'Clinical trials',
  dashboard: 'Dashboard6',
  accessRight: 'clinical-trials',
  visible: false
};

export default defineComponent({
  name: 'InternalOrderCancerView',
  props: {
    icd: String,
    icdPresentationString: String,
    icdDescription: String,
    diseaseDescription: String,
    projectDescription: String,
    meshIds: Array,
    meshDescription: String,
    dashboard1Included: {
      type: Boolean,
      default: true
    },
    dashboard2Included: {
      type: Boolean,
      default: true
    },
    dashboard3Included: {
      type: Boolean,
      default: true
    },
    dashboard4Included: {
      type: Boolean,
      default: true
    },
    dashboard5Included: {
      type: Boolean,
      default: true
    },
    dashboard6Included: {
      type: Boolean,
      default: true
    }
  },
  components: {
    InternalOrderCancerProjectOverview,
    InternalOrderCancerDemographicBaseline,
    InternalOrderCancerOverallSurvival,
    InternalOrderCancerTreatmentPatternInterventions,
    InternalOrderCancerTreatmentPaths,
    VueToPrint,
    ClinicalTrials
  },
  data: context => ({
    initializing: true,
    dashboard_1: DASHBOARD_1,
    dashboard_2: DASHBOARD_2,
    dashboard_3: DASHBOARD_3,
    dashboard_4: DASHBOARD_4,
    dashboard_5: DASHBOARD_5,
    dashboard_6: DASHBOARD_6,
    selected_menu_item: DASHBOARD_1,
    populationDefinition: [],
    select_age_at_diagnosis_items: [],
    selected_age_at_diagnosis: [],
    select_gender_items: [],
    selected_genders: [],
    select_diagnosis_date_items: [],
    selected_diagnosis_date: [],
    rules: {
      required(value) {
        if (value instanceof Array && (value.length === 0)) {
          return 'Required.';
        }
        return !!value || 'Required.';
      }
    },
    information_drawer: false,
    filter_drawer: false,
    menu_items: [
      context.dashboard1Included ? DASHBOARD_1 : null,
      context.dashboard2Included ? DASHBOARD_2 : null,
      context.dashboard3Included ? DASHBOARD_3 : null,
      context.dashboard4Included ? DASHBOARD_4 : null,
      context.dashboard5Included ? DASHBOARD_5 : null,
      context.dashboard6Included ? DASHBOARD_6 : null
    ].filter(v => v !== null)
  }),
  methods: {
    isAuthorized(accessRight) {
      return this.$store.getters.stateUser?.access_rights?.includes(accessRight)
    },
    toggleAllNoneAgeGroups() {
      if (this.selected_age_at_diagnosis.length === this.select_age_at_diagnosis_items.length) {
        this.selected_age_at_diagnosis = [];
      } else {
        this.selected_age_at_diagnosis = [...this.select_age_at_diagnosis_items]
      }
    },
    toggleAllOneAgeGroups(ageGroup) {
      if ((this.selected_age_at_diagnosis.length === 1) && (this.selected_age_at_diagnosis[0] === ageGroup)) {
        this.selected_age_at_diagnosis = [...this.select_age_at_diagnosis_items]
      } else {
        this.selected_age_at_diagnosis = [ageGroup]
      }
    },
    toggleAllNoneGenders() {
      if (this.selected_genders.length === this.select_gender_items.length) {
        this.selected_genders = [];
      } else {
        this.selected_genders = [...this.select_gender_items]
      }
    },
    toggleAllOneGenders(gender) {
      if ((this.selected_genders.length === 1) && (this.selected_genders[0] === gender)) {
        this.selected_genders = [...this.select_gender_items]
      } else {
        this.selected_genders = [gender]
      }
    },
    async loadPopulationDefinition() {
      return axios.get('/cancer-internal-order/population-definition',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadAgeAtDiagnosis() {
      return axios.get('/cancer-internal-order/ages-at-diagnosis',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadGenders() {
      return axios.get('/cancer-internal-order/genders',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadDiagnosisYears() {
      return axios.get('/cancer-internal-order/diagnosis-years',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadStaticData() {
      return await Promise.all([
        this.loadPopulationDefinition(),
        this.loadAgeAtDiagnosis(),
        this.loadGenders(),
        this.loadDiagnosisYears()
      ]).then((response) => {
        this.populationDefinition = [...response[0].data];
        this.select_age_at_diagnosis_items = [...response[1].data];
        this.selected_age_at_diagnosis = [...response[1].data];
        this.select_gender_items = [...response[2].data];
        this.selected_genders = [...response[2].data];
        this.select_diagnosis_date_items = [...response[3].data];
        this.selected_diagnosis_date = [...response[3].data];
        console.log('Static data loaded: InternalOrderCancerView')
      })
    },
    async loadDynamicData() {
    }
  },
  watch: {
    icd() {
      this.loadStaticData().then(() => {
        this.loadDynamicData()
      })
    }
  },
  async mounted() {
    this.initializing = true
    this.dashboard_1.visible = true
    this.dashboard_2.visible = true
    this.dashboard_3.visible = false
    this.dashboard_4.visible = false
    this.dashboard_5.visible = false
    this.dashboard_6.visible = false
    await this.loadStaticData().then(() => {
      this.initializing = false
      return this.loadDynamicData()
    }).finally(() => {
      this.initializing = false
    })
  }
})
</script>

<style scoped>
.text-block {
  white-space: pre-wrap;
}
</style>

